import React, { useState } from 'react';
import { CustomFlowbiteTheme, Modal, Textarea } from 'flowbite-react';
import Button from '../../components/Button';
import { SvgCloseCircle } from '../../../components/icons';
import { FlippingBook } from '../../api/flipping';

const formatFileSize = (bytes?: number) => {
  if (!bytes) return '\u2014';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface ReplacePDFModalProps {
  show: boolean;
  onClose: () => void;
  book: FlippingBook | null;
  file: File | null;
  onConfirm: (reason: string) => void;
  isProcessing: boolean;
}

const customTheme: CustomFlowbiteTheme['modal'] = {
  root: {
    show: {
      on: 'flex bg-neutral-900 bg-opacity-80',
      off: 'hidden',
    },
  },
  content: {
    base: 'relative h-full max-sm:w-full md:h-auto font-[figtree]',
  },
  body: {
    base: 'px-6',
  },
  footer: {
    base: 'flex space-x-2 rounded-b border-neutral-200 min-h-[91px] px-6 pb-[13px] pt-[34px] border-neutral-600',
  },
};

const ReplacePDFModal: React.FC<ReplacePDFModalProps> = ({
  show,
  onClose,
  book,
  file,
  onConfirm,
  isProcessing,
}) => {
  const [reason, setReason] = useState('');

  const handleClose = () => {
    if (isProcessing) return;
    setReason('');
    onClose();
  };

  const handleConfirm = () => {
    onConfirm(reason);
  };

  if (!book || !file) return null;

  return (
    <Modal
      theme={customTheme}
      show={show}
      onClose={handleClose}
      size="lg"
      popup
      dismissible={!isProcessing}
    >
      <div className="mx-6 pt-6 pb-3 flex justify-between items-center font-bold border-b border-neutral-200">
        <span className="text-base font-bold">Replace PDF File</span>
        <button
          className="p-2.5 bg-neutral-100 rounded-md cursor-pointer hover:bg-neutral-200"
          role="button"
          onClick={handleClose}
          disabled={isProcessing}
        >
          <SvgCloseCircle />
        </button>
      </div>
      <Modal.Body className="pt-[18px] text-sm font-figtree">
        <div className="flex flex-col gap-4">
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 text-sm font-medium">
              You are about to replace the PDF file for this book. This action
              will overwrite the existing file and trigger reprocessing.
            </p>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-bold text-neutral-800">Book Details</h4>
            <div className="p-3 bg-neutral-50 rounded-lg space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">Title</span>
                <span className="text-neutral-800 font-medium">
                  {book.info.title || 'Untitled'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">Customer Name</span>
                <span className="text-neutral-800 font-medium">
                  {book.customer?.name || '\u2014'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">Customer Email</span>
                <span className="text-neutral-800 font-medium">
                  {book.customer?.email || '\u2014'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">Book ID</span>
                <span className="text-neutral-800 font-medium font-mono text-xs">
                  {book.bookId}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-bold text-neutral-800">File Information</h4>
            <div className="p-3 bg-neutral-50 rounded-lg space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">Current File Size</span>
                <span className="text-neutral-800 font-medium">
                  {formatFileSize(book.fileSizeBytes)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">New File Size</span>
                <span className="text-neutral-800 font-medium">
                  {formatFileSize(file.size)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">New File Name</span>
                <span className="text-neutral-800 font-medium">{file.name}</span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="replace-reason"
              className="text-sm font-bold text-neutral-800"
            >
              Reason for Replacement (optional)
            </label>
            <Textarea
              id="replace-reason"
              placeholder="Enter a reason for replacing the PDF..."
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isProcessing}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="w-full flex items-center justify-end gap-2">
          <Button
            variant="OUTLINED"
            onClick={handleClose}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            variant="DANGER"
            onClick={handleConfirm}
            disabled={isProcessing}
            isProcessing={isProcessing}
          >
            Replace PDF
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ReplacePDFModal;
